import React, { useState, useEffect } from 'react';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import SyncableUrlInput, { UrlSyncActions } from '../../../../components/ui/SyncableUrlInput';
import { useOrganization } from '../../../../contexts/OrganizationContext';
import { useUrlSync } from '../../../../hooks/useUrlSync';

const StepThankYouPages = ({ campaignData, onDataChange, onSave, onMessage, onActiveTabChange }) => {
  const { currentOrganization } = useOrganization();
  const { 
    syncToOrganization, 
    syncToCampaign, 
    syncAllToOrganization,
    syncAllToCampaign,
    getEffectiveUrl, 
    getAllEffectiveUrls,
    hasUrlDifferences,
    PLATFORM_CONFIG 
  } = useUrlSync();
  const [activeTab, setActiveTab] = useState('high');
  const [validationErrors, setValidationErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Character limits for thank you page content
  const limits = {
    message: 500,
    contact_info: 200
  };

  // Initialize platform URLs from organization settings if not already set
  useEffect(() => {
    if (currentOrganization && campaignData && !campaignData.platform_urls) {
      const defaultPlatformUrls = {
        google: currentOrganization.google_review_url || '',
        yelp: currentOrganization.yelp_url || '',
        facebook: currentOrganization.facebook_url || ''
      };
      
      // Migrate existing review_platform_url if it exists
      if (campaignData.review_platform_url && campaignData.selected_review_platform) {
        defaultPlatformUrls[campaignData.selected_review_platform] = campaignData.review_platform_url;
      }
      
      onDataChange('platform_urls', defaultPlatformUrls);
    }

    // Initialize selected_review_platform from organization's primary platform if not already set
    if (currentOrganization && campaignData && !campaignData.selected_review_platform) {
      const orgPrimaryPlatform = currentOrganization.primary_review_platform;
      
      // Use organization's primary platform if available, otherwise fall back to Google
      const defaultPlatform = orgPrimaryPlatform || 'google';
      
      onDataChange('selected_review_platform', defaultPlatform);
    }

    // Initialize thank you pages if not present to prevent validation errors
    if (campaignData && !campaignData.thank_you_pages) {
      const defaultThankYouPages = {
        low: {
          headline: "We're sorry you had a poor experience",
          message: "Your feedback is important to us. Please let us know what went wrong so we can make it right.",
          buttonText: "Contact Us"
        },
        neutral: {
          headline: "Thank you for your feedback!",
          message: "We appreciate you taking the time to share your experience with us. Your feedback helps us continue to improve our services."
        },
        high: {
          headline: "Help others by sharing your experience!",
          message: "We've generated a review draft based on your feedback. Feel free to copy and customize it, then share it on your preferred platform."
        }
      };
      onDataChange('thank_you_pages', defaultThankYouPages);
    }
  }, [currentOrganization, campaignData, onDataChange]);

  // Notify parent when active tab changes for preview
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    if (onActiveTabChange) {
      onActiveTabChange(tabId);
    }
  };

  // Make ratingTabs reactive to threshold changes
  const ratingTabs = React.useMemo(() => [
    {
      id: 'low',
      label: '1-2 Stars',
      subtitle: 'Private feedback',
      color: 'bg-red-100 text-red-700 border-red-200'
    },
    {
      id: 'neutral', 
      label: '3 Stars',
      subtitle: 'Thank you page',
      color: 'bg-yellow-100 text-yellow-700 border-yellow-200'
    },
    {
      id: 'high',
      label: `${campaignData.positive_threshold || 4}+ Stars`,
      subtitle: 'AI review + platforms',
      color: 'bg-green-100 text-green-700 border-green-200'
    }
  ], [campaignData.positive_threshold]);

  // Validation functions
  const validateField = (field, value) => {
    const errors = { ...validationErrors };
    const key = `${activeTab}_${field}`;
    
    if (field === 'message') {
      if (!value || value.trim().length === 0) {
        errors[key] = 'Message is required';
      } else if (value.length > limits.message) {
        errors[key] = `Message must be ${limits.message} characters or less`;
      } else {
        delete errors[key];
      }
    } else if (field === 'contact_info') {
      if (value && value.length > limits.contact_info) {
        errors[key] = `Contact info must be ${limits.contact_info} characters or less`;
      } else {
        delete errors[key];
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Clear validation errors when campaign data changes to prevent stale validation
  useEffect(() => {
    if (campaignData?.thank_you_pages) {
      setValidationErrors({});
    }
  }, [campaignData?.thank_you_pages]);

  const handleThankYouPageChange = (ratingLevel, field, value) => {
    const updatedPages = {
      ...campaignData.thank_you_pages,
      [ratingLevel]: {
        ...campaignData.thank_you_pages[ratingLevel],
        [field]: value
      }
    };
    onDataChange('thank_you_pages', updatedPages);
    
    // Validate field on change
    setTimeout(() => validateField(field, value), 100);
  };

  // Handle platform URL changes
  const handlePlatformUrlChange = (platform, url) => {
    const updatedPlatformUrls = {
      ...campaignData.platform_urls,
      [platform]: url
    };
    onDataChange('platform_urls', updatedPlatformUrls);
  };

  // Handle sync to organization
  const handleSyncToOrg = async (platform, url) => {
    try {
      await syncToOrganization(platform, url);
      onMessage('success', `${PLATFORM_CONFIG[platform]?.label} URL saved as organization default!`);
    } catch (error) {
      console.error('Sync to organization error:', error);
      onMessage('error', `Failed to save ${PLATFORM_CONFIG[platform]?.label} URL as default`);
    }
  };

  // Handle sync from organization
  const handleSyncFromOrg = async (platform) => {
    try {
      const updatedUrls = await syncToCampaign(platform, campaignData.platform_urls);
      onDataChange('platform_urls', updatedUrls);
      onMessage('success', `Using organization default for ${PLATFORM_CONFIG[platform]?.label}`);
    } catch (error) {
      console.error('Sync from organization error:', error);
      onMessage('error', `Failed to sync ${PLATFORM_CONFIG[platform]?.label} URL from organization`);
    }
  };

  // Handle sync all to organization
  const handleSyncAllToOrg = async () => {
    try {
      await syncAllToOrganization(campaignData.platform_urls);
      onMessage('success', 'All campaign URLs saved as organization defaults!');
    } catch (error) {
      console.error('Sync all to organization error:', error);
      onMessage('error', 'Failed to sync all URLs to organization');
    }
  };

  // Handle sync all from organization
  const handleSyncAllFromOrg = async () => {
    try {
      const updatedUrls = syncAllToCampaign(campaignData.platform_urls);
      onDataChange('platform_urls', updatedUrls);
      onMessage('success', 'Using organization defaults for all platform URLs');
    } catch (error) {
      console.error('Sync all from organization error:', error);
      onMessage('error', 'Failed to sync all URLs from organization');
    }
  };

  const renderFieldError = (ratingLevel, field) => {
    const key = `${ratingLevel}_${field}`;
    if (validationErrors[key]) {
      return (
        <div className="mt-1 flex items-center text-red-600 text-sm">
          <Icon name="AlertCircle" size={14} className="mr-1" />
          {validationErrors[key]}
        </div>
      );
    }
    return null;
  };

  const renderCharacterCount = (field, value) => {
    const count = value ? value.length : 0;
    const limit = limits[field];
    const isNearLimit = count > limit * 0.8;
    const isOverLimit = count > limit;
    
    return (
      <div className={`text-xs ${isOverLimit ? 'text-red-600' : isNearLimit ? 'text-yellow-600' : 'text-gray-500'}`}>
        {count}/{limit} characters
      </div>
    );
  };

  const renderLowRatingTab = () => (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-semibold text-gray-900 mb-2">Private Feedback (1-2 Stars)</h4>
        <p className="text-gray-600">
          When customers give low ratings, collect private feedback for service recovery.
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h5 className="font-medium text-gray-900 mb-4">Page Content</h5>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Headline
            </label>
            <input
              type="text"
              value={campaignData.thank_you_pages?.low?.headline || "We're sorry you had a poor experience"}
              onChange={(e) => handleThankYouPageChange('low', 'headline', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="We're sorry you had a poor experience"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Message
              </label>
              {renderCharacterCount('message', campaignData.thank_you_pages?.low?.message)}
            </div>
            <textarea
              value={campaignData.thank_you_pages?.low?.message || "Your feedback is important to us. Please let us know what went wrong so we can make it right."}
              onChange={(e) => handleThankYouPageChange('low', 'message', e.target.value)}
              rows={4}
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent resize-none ${
                validationErrors[`low_message`] ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Your feedback is important to us. Please let us know what went wrong so we can make it right."
              maxLength={limits.message}
            />
            {renderFieldError('low', 'message')}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contact Button Text
            </label>
            <input
              type="text"
              value={campaignData.thank_you_pages?.low?.buttonText || "Contact Us"}
              onChange={(e) => handleThankYouPageChange('low', 'buttonText', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Contact Us"
            />
          </div>
        </div>

        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start space-x-2">
            <Icon name="Info" size={16} className="text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <strong>Automatic Actions:</strong> When customers submit 1-2 star reviews, an internal alert email is automatically sent to help with service recovery.
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderNeutralRatingTab = () => (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-semibold text-gray-900 mb-2">Thank You Page (3 Stars)</h4>
        <p className="text-gray-600">
          Simple thank you message for neutral ratings.
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h5 className="font-medium text-gray-900 mb-4">Page Content</h5>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Headline
            </label>
            <input
              type="text"
              value={campaignData.thank_you_pages?.neutral?.headline || "Thank you for your feedback!"}
              onChange={(e) => handleThankYouPageChange('neutral', 'headline', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Thank you for your feedback!"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Message
              </label>
              {renderCharacterCount('message', campaignData.thank_you_pages?.neutral?.message)}
            </div>
            <textarea
              value={campaignData.thank_you_pages?.neutral?.message || "We appreciate you taking the time to share your experience with us. Your feedback helps us continue to improve our services."}
              onChange={(e) => handleThankYouPageChange('neutral', 'message', e.target.value)}
              rows={4}
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent resize-none ${
                validationErrors[`neutral_message`] ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="We appreciate you taking the time to share your experience with us."
              maxLength={limits.message}
            />
            {renderFieldError('neutral', 'message')}
          </div>
        </div>
      </div>
    </div>
  );

  const renderHighRatingTab = () => (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-semibold text-gray-900 mb-2">AI Review & External Platforms ({campaignData.positive_threshold || 4}+ Stars)</h4>
        <p className="text-gray-600">
          Generate AI-powered review drafts and encourage external platform reviews.
        </p>
      </div>

      {/* AI Review Draft Settings - Always enabled for high ratings */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h5 className="font-medium text-gray-900 mb-4">AI Review Draft</h5>
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Intro Message
                <span className="text-xs text-gray-500 ml-1">(appears below "Feedback Submitted!" headline)</span>
              </label>
              {renderCharacterCount('message', campaignData.thank_you_pages?.high?.message)}
            </div>
            <textarea
              value={campaignData.thank_you_pages?.high?.message || "We've generated a review draft based on your feedback. Feel free to copy and customize it, then share it on your preferred platform."}
              onChange={(e) => handleThankYouPageChange('high', 'message', e.target.value)}
              rows={3}
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent resize-none ${
                validationErrors[`high_message`] ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="We've generated a review draft based on your feedback."
              maxLength={limits.message}
            />
            {renderFieldError('high', 'message')}
          </div>

        </div>
      </div>

      {/* External Platform Selection */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h5 className="font-medium text-gray-900 mb-4">External Platform</h5>
        <p className="text-sm text-gray-600 mb-4">
          Choose one platform where customers will be directed to leave public reviews.
        </p>

        {/* Bulk Sync Actions */}
        <UrlSyncActions
          hasDifferences={hasUrlDifferences(campaignData.platform_urls)}
          onSyncAllToOrg={handleSyncAllToOrg}
          onSyncAllFromOrg={handleSyncAllFromOrg}
          disabled={isLoading}
          className="mb-6"
        />

        <div className="space-y-4">
          {/* Platform Selection Toggles */}
          <div className="space-y-3">
            {/* Google */}
            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">G</span>
                </div>
                <div>
                  <div className="font-medium text-gray-900">Google Business Profile</div>
                  <div className="text-sm text-gray-600">Recommended - highest impact</div>
                </div>
              </div>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="review_platform"
                  checked={(campaignData.selected_review_platform || currentOrganization?.primary_review_platform || 'google') === 'google'}
                  onChange={() => onDataChange('selected_review_platform', 'google')}
                  className="rounded border-gray-300 text-primary focus:ring-primary mr-2"
                />
              </label>
            </div>

            {/* Yelp */}
            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">Y</span>
                </div>
                <div>
                  <div className="font-medium text-gray-900">Yelp</div>
                  <div className="text-sm text-gray-600">Great for restaurants & services</div>
                </div>
              </div>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="review_platform"
                  checked={(campaignData.selected_review_platform || currentOrganization?.primary_review_platform || 'google') === 'yelp'}
                  onChange={() => onDataChange('selected_review_platform', 'yelp')}
                  className="rounded border-gray-300 text-primary focus:ring-primary mr-2"
                />
              </label>
            </div>

            {/* Facebook */}
            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-800 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">F</span>
                </div>
                <div>
                  <div className="font-medium text-gray-900">Facebook</div>
                  <div className="text-sm text-gray-600">Social media reviews</div>
                </div>
              </div>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="review_platform"
                  checked={(campaignData.selected_review_platform || currentOrganization?.primary_review_platform || 'google') === 'facebook'}
                  onChange={() => onDataChange('selected_review_platform', 'facebook')}
                  className="rounded border-gray-300 text-primary focus:ring-primary mr-2"
                />
              </label>
            </div>
          </div>

          {/* Enhanced URL Input for Selected Platform */}
          {campaignData.selected_review_platform && (
            <div className="mt-6">
              <SyncableUrlInput
                platform={campaignData.selected_review_platform}
                value={campaignData.platform_urls?.[campaignData.selected_review_platform] || ''}
                onChange={(e) => handlePlatformUrlChange(campaignData.selected_review_platform, e.target.value)}
                orgDefault={currentOrganization?.[PLATFORM_CONFIG[campaignData.selected_review_platform]?.orgField] || ''}
                isOverride={!!(campaignData.platform_urls?.[campaignData.selected_review_platform])}
                source={getEffectiveUrl(campaignData.selected_review_platform, campaignData.platform_urls).source}
                onSyncToOrg={handleSyncToOrg}
                onSyncFromOrg={handleSyncFromOrg}
                disabled={isLoading}
                showSyncButtons={true}
                placeholder={PLATFORM_CONFIG[campaignData.selected_review_platform]?.placeholder}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'low':
        return renderLowRatingTab();
      case 'neutral':
        return renderNeutralRatingTab();
      case 'high':
        return renderHighRatingTab();
      default:
        return renderLowRatingTab();
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Feedback</h3>
        <p className="text-gray-600">
          Configure the experience customers see after submitting their review based on their rating.
        </p>
      </div>

      {/* Rating Level Tabs */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="flex border-b border-gray-200">
          {ratingTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`flex-1 px-6 py-4 text-left border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary bg-primary bg-opacity-5'
                  : 'border-transparent hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className={`text-sm font-medium ${
                    activeTab === tab.id ? 'text-primary' : 'text-gray-900'
                  }`}>
                    {tab.label}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {tab.subtitle}
                  </div>
                </div>
                <div className={`px-2 py-1 rounded-full text-xs font-medium border ${tab.color}`}>
                  {tab.label}
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="p-6">
          {renderTabContent()}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          variant="primary"
          disabled={isLoading || Object.keys(validationErrors).length > 0}
          onClick={async () => {
            // Validate all required fields
            const hasErrors = Object.keys(validationErrors).length > 0;
            console.log('Validation errors:', validationErrors);
            console.log('Has errors:', hasErrors);
            
            if (hasErrors) {
              if (onMessage) {
                onMessage('error', 'Please fix validation errors before saving.');
              }
              return;
            }

            setIsLoading(true);
            try {
              if (onSave) {
                // Pass step-specific data
                const stepData = {
                  thank_you_pages: campaignData.thank_you_pages,
                  selected_review_platform: campaignData.selected_review_platform,
                  platform_urls: campaignData.platform_urls
                };
                console.log('Saving step data:', stepData);
                console.log('Campaign data:', campaignData);
                await onSave(stepData);
              }
              if (onMessage) {
                onMessage('success', 'Thank you page settings saved successfully!');
              }
            } catch (error) {
              console.error('Save error:', error);
              if (onMessage) {
                onMessage('error', 'Failed to save settings. Please try again.');
              }
            } finally {
              setIsLoading(false);
            }
          }}
        >
          {isLoading ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </div>
          ) : (
            'Save Settings'
          )}
        </Button>
      </div>
    </div>
  );
};

export default StepThankYouPages; 